import React from 'react';
import NavigationHeader from '../components/NavigationHeader';
import Footer from '../components/Footer';
import HowToBook from '../components/HowToBook';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Check, Star, Info, AlertTriangle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { pricingData, startDates } from '../siteData';
import { useLanguage } from '../contexts/LanguageContext';
import { getTranslation } from '../translations';

const PricesPage = () => {
  const { language } = useLanguage();
  const t = (key) => getTranslation(language, key);

  // Get localized path
  const getLocalizedPath = (path) => {
    if (language === 'es') return `/es${path}`;
    if (language === 'fr') return `/fr${path}`;
    return path;
  };

  // Translate duration
  const translateDuration = (duration) => {
    const durationMap = {
      '1 Week': t('prices.oneWeek'),
      '2 Weeks': t('prices.twoWeeks'),
      '3 Weeks': t('prices.threeWeeks'),
      '4 Weeks': t('prices.fourWeeks')
    };
    return durationMap[duration] || duration;
  };

  // Get translated clinic features based on duration
  const getClinicFeatures = (duration) => {
    const clinicFeatures = t('prices.clinicFeatures') || {};
    switch(duration) {
      case '1 Week':
        return [
          clinicFeatures['20hours'],
          clinicFeatures['english45'],
          clinicFeatures['fullBoard'],
          clinicFeatures['stadiumExcursion']
        ];
      case '2 Weeks':
        return [
          clinicFeatures['40hours'],
          clinicFeatures['english45'],
          clinicFeatures['fullBoard'],
          clinicFeatures['stadiumExcursions2'],
          clinicFeatures['tournament']
        ];
      case '3 Weeks':
        return [
          clinicFeatures['60hours'],
          clinicFeatures['english45'],
          clinicFeatures['fullBoard'],
          clinicFeatures['stadiumExcursions3'],
          clinicFeatures['analysisReport']
        ];
      case '4 Weeks':
        return [
          clinicFeatures['80hours'],
          clinicFeatures['english45'],
          clinicFeatures['fullBoard'],
          clinicFeatures['stadiumExcursions4'],
          clinicFeatures['completeAnalysis'],
          clinicFeatures['certificate']
        ];
      default:
        return [];
    }
  };

  // Get translated villa features based on duration
  const getVillaFeatures = (duration) => {
    const villaFeatures = t('prices.villaFeatures') || {};
    switch(duration) {
      case '1 Week':
        return [
          villaFeatures['officialCoaching'],
          villaFeatures['20hours'],
          villaFeatures['villaParkVisit'],
          villaFeatures['fullKit']
        ];
      case '2 Weeks':
        return [
          villaFeatures['officialCoaching'],
          villaFeatures['40hours'],
          villaFeatures['villaParkVisit'],
          villaFeatures['fullKit'],
          villaFeatures['evaluation'],
          villaFeatures['certificate']
        ];
      default:
        return [];
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <NavigationHeader />
      
      {/* Hero */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-green-600 to-green-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-black mb-6">{t('prices.heroTitle')}</h1>
          <p className="text-xl text-green-100 max-w-3xl mx-auto">
            {t('prices.heroSubtitle')}
          </p>
        </div>
      </section>

      {/* High Performance Clinic Pricing */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-black text-gray-900 mb-4">{t('prices.highPerformanceClinic')}</h2>
            <p className="text-xl text-gray-600">{t('prices.chooseDuration')}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {pricingData.clinic.map((plan, index) => (
              <Card 
                key={index}
                className={`relative ${
                  plan.popular 
                    ? 'bg-white border-4 border-green-500 transform scale-105 shadow-2xl' 
                    : 'bg-white hover:shadow-xl transition-all duration-300'
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <div className="bg-green-500 text-white px-4 py-1 rounded-full text-xs font-black flex items-center shadow-lg">
                      <Star className="h-3 w-3 mr-1 fill-current" />
                      {t('prices.mostPopular')}
                    </div>
                  </div>
                )}
                
                <div className="p-6">
                  <div className="text-center mb-6">
                    <h3 className="text-2xl font-black mb-2 text-gray-900">{translateDuration(plan.duration)}</h3>
                    <div className="text-4xl font-black mb-1 text-green-600">{plan.price}</div>
                    <p className="text-xs text-gray-600">{t('prices.fullBoardIncluded')}</p>
                  </div>

                  <ul className="space-y-3 mb-6">
                    {getClinicFeatures(plan.duration).map((feature, idx) => (
                      <li key={idx} className="flex items-start text-sm">
                        <Check className="h-4 w-4 mr-2 flex-shrink-0 mt-0.5 text-green-600" />
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <Link to={getLocalizedPath('/signup')}>
                    <Button 
                      className={`w-full font-bold ${
                        plan.popular 
                          ? 'bg-green-600 hover:bg-green-700 text-white' 
                          : 'bg-gray-900 hover:bg-gray-800 text-white'
                      }`}
                    >
                      {t('prices.signUpNow')}
                    </Button>
                  </Link>
                </div>
              </Card>
            ))}
          </div>

          {/* Start Dates */}
          <Card className="p-8 bg-white">
            <h3 className="text-2xl font-black text-gray-900 mb-2 text-center">{t('prices.startDates2026')}</h3>
            <p className="text-sm text-center text-orange-600 font-semibold mb-6">{t('prices.limitedAvailability')}</p>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {startDates.map((dateInfo, index) => {
                const isSoldOut = dateInfo.soldOut;
                const isClinicSoldOut = dateInfo.clinicSoldOut;
                const hasLowSpots = dateInfo.spotsLeft > 0 && dateInfo.spotsLeft <= 10;

                return (
                  <div 
                    key={index}
                    className={`rounded-lg p-4 text-center border-2 transition-all duration-200 ${
                      isSoldOut
                        ? 'bg-gray-100 border-gray-300 opacity-75'
                        : hasLowSpots
                          ? 'bg-orange-50 border-orange-300 hover:border-orange-500'
                          : 'bg-green-50 border-green-200 hover:border-green-500'
                    }`}
                    data-testid={`start-date-${index}`}
                  >
                    <div className={`text-lg font-black mb-1 ${isSoldOut ? 'text-gray-400' : 'text-gray-900'}`}>
                      {dateInfo.date}
                    </div>
                    <div className="text-xs font-semibold mb-1">
                      {dateInfo.programs.map(p => p === 'clinic' ? 'Clinic' : 'Aston Villa').join(' + ')}
                    </div>
                    {isSoldOut ? (
                      <div className="inline-flex items-center gap-1 bg-red-100 text-red-700 text-xs font-bold px-2 py-0.5 rounded-full mt-1" data-testid={`sold-out-badge-${index}`}>
                        {t('prices.soldOut')}
                      </div>
                    ) : isClinicSoldOut ? (
                      <div className="space-y-1 mt-1">
                        <div className="inline-flex items-center gap-1 bg-red-100 text-red-700 text-xs font-bold px-2 py-0.5 rounded-full" data-testid={`clinic-sold-out-badge-${index}`}>
                          {t('prices.clinicSoldOut')}
                        </div>
                        <div className="inline-flex items-center gap-1 bg-orange-100 text-orange-700 text-xs font-bold px-2 py-0.5 rounded-full">
                          <AlertTriangle className="h-3 w-3" />
                          {t('prices.astonVillaAvailable').replace('{x}', dateInfo.spotsLeft)}
                        </div>
                      </div>
                    ) : hasLowSpots ? (
                      <div className="inline-flex items-center gap-1 bg-orange-100 text-orange-700 text-xs font-bold px-2 py-0.5 rounded-full mt-1" data-testid={`low-spots-badge-${index}`}>
                        <AlertTriangle className="h-3 w-3" />
                        {dateInfo.spotsLeft} {t('prices.spotsLeft')}
                      </div>
                    ) : (
                      <div className="text-xs text-green-600 font-semibold mt-1">{t('prices.available')}</div>
                    )}
                  </div>
                );
              })}
            </div>
          </Card>
        </div>
      </section>

      {/* Aston Villa Pricing */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-black text-gray-900 mb-4">{t('prices.astonVillaProgram')}</h2>
            <p className="text-xl text-gray-600">{t('prices.astonVillaDesc')}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-16 max-w-4xl mx-auto">
            {pricingData.astonVilla.map((plan, index) => (
              <Card 
                key={index}
                className={`relative ${
                  plan.popular 
                    ? 'bg-white border-4 border-purple-800 transform scale-105 shadow-2xl' 
                    : 'bg-white hover:shadow-xl transition-all duration-300'
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <div className="bg-purple-800 text-white px-4 py-1 rounded-full text-xs font-black flex items-center shadow-lg">
                      <Star className="h-3 w-3 mr-1 fill-current" />
                      {t('prices.mostPopular')}
                    </div>
                  </div>
                )}
                
                <div className="p-6">
                  <div className="text-center mb-6">
                    <h3 className="text-2xl font-black mb-2 text-gray-900">{translateDuration(plan.duration)}</h3>
                    <div className="text-4xl font-black mb-1 text-purple-800">{plan.price}</div>
                    <p className="text-xs text-gray-600">{t('prices.fullBoardTrainingKit')}</p>
                  </div>

                  {/* Scarcity badge */}
                  <div className="flex justify-center mb-4">
                    <div className="inline-flex items-center gap-1.5 bg-orange-50 border border-orange-200 text-orange-700 text-xs font-bold px-3 py-1.5 rounded-full" data-testid={`villa-spots-badge-${index}`}>
                      <AlertTriangle className="h-3 w-3" />
                      <span>{t('prices.onlyXSpotsLeft').replace('{x}', '8')}</span>
                    </div>
                  </div>

                  <ul className="space-y-3 mb-6">
                    {getVillaFeatures(plan.duration).map((feature, idx) => (
                      <li key={idx} className="flex items-start text-sm">
                        <Check className="h-4 w-4 mr-2 flex-shrink-0 mt-0.5 text-purple-800" />
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <Link to={getLocalizedPath('/signup')}>
                    <Button 
                      className="w-full font-bold bg-gradient-to-r from-purple-800 to-red-700 hover:from-purple-900 hover:to-red-800 text-white"
                    >
                      {t('prices.signUpNow')}
                    </Button>
                  </Link>
                </div>
              </Card>
            ))}
          </div>

          {/* Registration Info */}
          <Card className="p-8 bg-gradient-to-br from-purple-50 to-red-50 border-2 border-purple-200 max-w-4xl mx-auto">
            <h3 className="text-xl font-black text-gray-900 mb-4 text-center">{t('prices.registrationInfo')}</h3>
            <div className="grid md:grid-cols-3 gap-4 text-sm text-gray-700">
              <div className="text-center">
                <p className="font-bold text-purple-800 mb-1">{t('prices.registrationFee')}</p>
                <p>£100</p>
              </div>
              <div className="text-center">
                <p className="font-bold text-purple-800 mb-1">{t('prices.initialDeposit')}</p>
                <p>50% deposit</p>
                <p className="text-xs text-green-700 font-semibold mt-1">{t('prices.freeCancellation')}</p>
              </div>
              <div className="text-center">
                <p className="font-bold text-purple-800 mb-1">{t('prices.paymentOptions')}</p>
                <p>{t('prices.gbpEurUsd')}</p>
              </div>
            </div>
            <p className="text-xs text-gray-500 text-center mt-4">
              {t('prices.paymentsAccepted')}
            </p>
            <p className="text-xs text-gray-600 text-center mt-2">
              {t('prices.groupDiscounts')}
            </p>
          </Card>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Info className="h-12 w-12 text-green-600 mx-auto mb-4" />
            <h2 className="text-4xl font-black text-gray-900 mb-4">{t('prices.whatsIncluded')}</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            <Card className="p-6">
              <h3 className="text-lg font-black text-gray-900 mb-4">{t('prices.accommodationTitle')}</h3>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-start"><span className="text-green-600 mr-2">✓</span>{t('prices.fullBoard')}</li>
                <li className="flex items-start"><span className="text-green-600 mr-2">✓</span>{t('prices.sharedAccommodation')}</li>
                <li className="flex items-start"><span className="text-green-600 mr-2">✓</span>{t('prices.linenTowels')}</li>
                <li className="flex items-start"><span className="text-green-600 mr-2">✓</span>{t('prices.supervision')}</li>
              </ul>
            </Card>

            <Card className="p-6">
              <h3 className="text-lg font-black text-gray-900 mb-4">{t('prices.trainingEducation')}</h3>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-start"><span className="text-green-600 mr-2">✓</span>{t('prices.dailyTrainingSessions')}</li>
                <li className="flex items-start"><span className="text-green-600 mr-2">✓</span>{t('prices.englishLessons')}</li>
                <li className="flex items-start"><span className="text-green-600 mr-2">✓</span>{t('prices.uefaCoaches')}</li>
                <li className="flex items-start"><span className="text-green-600 mr-2">✓</span>{t('prices.evaluationReport')}</li>
              </ul>
            </Card>

            <Card className="p-6">
              <h3 className="text-lg font-black text-gray-900 mb-4">{t('prices.activitiesExtras')}</h3>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-start"><span className="text-green-600 mr-2">✓</span>{t('prices.stadiumExcursion')}</li>
                <li className="flex items-start"><span className="text-green-600 mr-2">✓</span>{t('prices.facilitiesAccess')}</li>
                <li className="flex items-start"><span className="text-green-600 mr-2">✓</span>{t('prices.eveningActivities')}</li>
                <li className="flex items-start"><span className="text-green-600 mr-2">✓</span>{t('prices.freeWifi')}</li>
              </ul>
            </Card>
          </div>

          <div className="mt-12 text-center">
            <p className="text-gray-600 mb-4">{t('prices.readyToSecure')}</p>
            <Link to="/signup">
              <Button size="lg" className="bg-green-600 hover:bg-green-700 text-white font-bold text-lg px-10 py-6">
                {t('prices.signUpNow')}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <HowToBook />

      <Footer />
    </div>
  );
};

export default PricesPage;
